var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
/* eslint-disable lit-a11y/click-events-have-key-events */
import { html, nothing } from 'lit';
import { property, state } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { ref, createRef } from 'lit/directives/ref.js';
import { repeat } from 'lit/directives/repeat.js';
import { when } from 'lit/directives/when.js';
// Components
import { AppBar } from '@frontend_zz/app-bar/AppBar.js';
import { BaseElement } from '@frontend_zz/base-element/BaseElement.js';
import { Button } from '@frontend_zz/button/Button.js';
import { Spinner } from '@frontend_zz/spinner/Spinner.js';
import { Surface } from '@frontend_zz/surface/Surface.js';
import { Text } from '@frontend_zz/text/Text.js';
// Utils
import getActiveElement from '@frontend_zz/web-component-utilities/getActiveElement.js';
// Styling
import styles from './Modal.css.js';
import { litFlex, litGap } from '@frontend_zz/web-component-utilities/styles/utility/layout.css.js';
export const modalSizeOptions = ['compact', 'large', 'auto'];
// Data
import content from './Modal.content.json';
import config from './Modal.config.json';
/**
 * @element zz-modal
 * @todo: rename to something that does not imply the modality, e.g. `overlay`
 */
export class Modal extends BaseElement {
    constructor() {
        super(...arguments);
        this.modal = false;
        this.surfaceVariant = 'elevated-not-clickable-under';
        this.overflow = false;
        this.visible = false;
        this.buttons = [];
        this.fullscreen = false;
        this.loading = false;
        this.loadingText = '';
        this.theme = config.defaultThemeName;
        this.preventUserDismiss = false;
        this.dialogRef = createRef();
        this.previouslyActiveElement = null;
        this.updated = (changedProps) => {
            const { dialogRef, visible, previouslyActiveElement } = this;
            // a11y: focus dialog on activation
            if (changedProps.has('visible') && visible) {
                this.previouslyActiveElement = getActiveElement();
                dialogRef?.value?.focus();
                this.dispatchEvent(new Event('display'));
            }
            // a11y: on deactivation, restore focus to previously active element
            if (changedProps.has('visible') && !visible) {
                previouslyActiveElement?.focus();
                this.previouslyActiveElement = null;
            }
        };
        /**
         * This private function is re-assigned with the Promise resolver
         */
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        this.resolveInteraction = () => { };
        /**
         * Function with public accessor
         * This can be awaited to resolve
         */
        this.interaction = async () => {
            return await new Promise((resolve) => {
                // Re-assign the Promise resolver
                this.resolveInteraction = resolve;
            });
        };
        /*
         * Event Handlers
         */
        this.onBackdropClick = (event) => {
            event.preventDefault();
            this.handleClose();
        };
        this.onCloseClick = (event) => {
            event.preventDefault();
            this.handleClose();
        };
        this.onKeyDown = (event) => {
            if (event.key === 'Escape' || event.key === 'Esc') {
                this.handleClose();
            }
        };
        this.onTransitionEnd = (event) => {
            const { propertyName } = event;
            if (propertyName === 'opacity' && !this.visible) {
                this.dispatchEvent(new Event('dismissed'));
            }
        };
        /**
         * Functions
         */
        this.handleClose = () => {
            this.onInteraction('dismiss');
            this.dispatchEvent(new Event('dismiss'));
        };
        this.render = () => {
            const { renderMain, renderHeader, renderContent, renderFooter } = this;
            return renderMain(html `${renderHeader()}${renderContent()}${renderFooter()}`);
        };
        this.renderMain = (body) => {
            const { modal, heading, theme, surfaceVariant, dialogRef, preventUserDismiss, onBackdropClick, onTransitionEnd, } = this;
            return html `
            ${when(modal, () => html `<div class="backdrop" @click=${!preventUserDismiss ? onBackdropClick : undefined}></div>`)}
            <div class="body">
                <zz-surface
                    ${ref(dialogRef)}
                    variant=${surfaceVariant}
                    theme=${theme}
                    class="surface"
                    role="dialog"
                    tabindex="0"
                    aria-modal="true"
                    aria-labelledby=${heading ? 'modal-heading' : ''}
                    aria-describedby="modal-content"
                    @transitionend=${onTransitionEnd}
                >
                    ${body}
                </zz-surface>
            </div>
        `;
        };
        this.renderMobileHeader = () => {
            const { heading, preventUserDismiss, onCloseClick } = this;
            return html `
            <zz-app-bar class="edit-modal-header" title=${ifDefined(heading)}>
                <div slot="left-navigation">
                    ${when(!preventUserDismiss, () => html `
                            <zz-button
                                variant="text"
                                icon="chevronLeft"
                                icon-size="s"
                                @click=${onCloseClick}
                                screenreader-label
                            >
                                ${content.close}
                            </zz-button>
                        `)}
                </div>
            </zz-app-bar>
        `;
        };
        this.renderDesktopHeader = () => {
            const { heading, preventUserDismiss, onCloseClick } = this;
            if (!heading && preventUserDismiss)
                return nothing;
            return html `
            <header class="modal-header">
                ${when(!preventUserDismiss, () => html `
                        <zz-button
                            variant="text"
                            icon="closeMini"
                            size="small"
                            class="modal-close"
                            screenreader-label
                            @click=${onCloseClick}
                        >
                            ${content.close}
                        </zz-button>
                    `)}
                ${heading
                ? html `<zz-text class="modal-heading" id="modal-heading" tag="h2">${heading}</zz-text>`
                : nothing}
            </header>
        `;
        };
        this.renderHeader = () => {
            const { renderMobileHeader, renderDesktopHeader, isMobile, fullscreen } = this;
            return isMobile && fullscreen ? renderMobileHeader() : renderDesktopHeader();
        };
        this.renderContent = () => {
            const { loading, loadingText } = this;
            return html `
            <div id="modal-content" class="modal-content">
                <slot></slot>
                ${when(loading, () => html `<div class="spinner-container" role="status" aria-live="polite">
                        <zz-spinner class="spinner" text=${loadingText}></zz-spinner>
                    </div>`)}
            </div>
        `;
        };
        this.renderButton = ({ icon, label, variant, type, href, eventType }) => {
            const onClick = (_event) => {
                this.dispatchEvent(new Event(eventType));
                this.onInteraction(eventType, _event);
            };
            return html `
            <zz-button
                variant=${ifDefined(variant)}
                type=${ifDefined(type)}
                icon=${ifDefined(icon)}
                href=${ifDefined(href)}
                @click=${onClick}
            >
                ${label}
            </zz-button>
        `;
        };
        this.renderFooter = () => {
            const { buttons, renderButton } = this;
            if (!buttons.length)
                return nothing;
            return html `
            <footer class="modal-footer flex flex-wrap flex-grow-children flex-justify-center gap-m">
                ${repeat(buttons, renderButton)}
            </footer>
        `;
        };
        /**
         * Scroll content slot to given element
         * @param element
         */
        this.scrollToElement = (element) => {
            const header = this.shadowRoot?.querySelector('.modal-header');
            const headerHeight = header && header.getBoundingClientRect().height;
            const modalContent = this.shadowRoot?.querySelector('#modal-content');
            const elementTop = element.getBoundingClientRect().top;
            modalContent?.scrollTo({
                top: elementTop - (headerHeight || 0),
                behavior: 'smooth',
            });
        };
    }
    static get scopedElements() {
        return {
            'zz-app-bar': AppBar,
            'zz-button': Button,
            'zz-spinner': Spinner,
            'zz-surface': Surface,
            'zz-text': Text,
        };
    }
    static get styles() {
        return [styles, litFlex, litGap];
    }
    /*
     * Lifecycle
     */
    connectedCallback() {
        super.connectedCallback();
        if (this.preventUserDismiss)
            return;
        const { onKeyDown } = this;
        document.addEventListener('keydown', onKeyDown);
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        if (this.preventUserDismiss)
            return;
        const { onKeyDown } = this;
        document.removeEventListener('keydown', onKeyDown);
    }
    onInteraction(response, _event) {
        const { resolveInteraction } = this;
        resolveInteraction(response);
    }
}
__decorate([
    property({ type: Boolean, reflect: true })
], Modal.prototype, "modal", void 0);
__decorate([
    property({ type: String, reflect: true })
], Modal.prototype, "surfaceVariant", void 0);
__decorate([
    property({ type: Boolean })
], Modal.prototype, "overflow", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], Modal.prototype, "visible", void 0);
__decorate([
    property({ reflect: true })
], Modal.prototype, "size", void 0);
__decorate([
    property({ type: String })
], Modal.prototype, "heading", void 0);
__decorate([
    property({ attribute: false })
], Modal.prototype, "buttons", void 0);
__decorate([
    property({ type: Boolean, reflect: true, attribute: 'fullscreen' })
], Modal.prototype, "fullscreen", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], Modal.prototype, "loading", void 0);
__decorate([
    property()
], Modal.prototype, "loadingText", void 0);
__decorate([
    property({ reflect: true })
], Modal.prototype, "theme", void 0);
__decorate([
    property({ type: Boolean, attribute: 'prevent-user-dismiss' })
], Modal.prototype, "preventUserDismiss", void 0);
__decorate([
    state()
], Modal.prototype, "dialogRef", void 0);
__decorate([
    state()
], Modal.prototype, "previouslyActiveElement", void 0);
